import React, { useRef, useState } from "react";
import {
  Box,
  MenuItem,
  TextField,
  Grid,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import Button from "../ui/elements/buttons/Button";
import { Distributor } from "../modules/distributor/distributorType/Distributor";
import { FilterValues } from "./schema/EventFiltersType";
import { ratingMapper } from "./ratingMapper";
import { eventTypeMapper } from "./schema/eventTypeMapper";
import { broadcastTypes } from "./schema/broadcastTypes";
import { Region } from "../modules/regions/regionType/Region";
import { useRegion } from "../modules/regions/queries/useRegion";

interface FilterPageProps {
  onClose: () => void;
  setFilters?: any;
  distributors?: Distributor[];
  formik:  FormikProps<FilterValues>;
}

const useBlurOnMinuteChange = () => {
  const prevValuesRef = useRef<{ [key: string]: string }>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
    setValue: (val: string) => void
  ) => {
    const newVal = e.target.value;
    const prevVal = prevValuesRef.current[key];

    prevValuesRef.current[key] = newVal;
    setValue(newVal);

    if (!prevVal || prevVal.length < 16 || newVal.length < 16) return;

    const prevMinutes = prevVal.slice(14, 16);
    const newMinutes = newVal.slice(14, 16);

    if (prevMinutes !== newMinutes) {
      e.target.blur();
    }
  };

  return { handleChange };
};

const FilterPage: React.FC<FilterPageProps> = ({
  onClose,
  setFilters,
  distributors,
  formik,
}) => {
  const distributorNames: string[] =
    distributors?.map((item) => item.name) || [];
const { data: regions } = useRegion();
  const { handleChange } = useBlurOnMinuteChange();

  return (
    <Box
      sx={{
        height: "100%",
        width: { sm: "500px", xs: "100%" },
        padding: "16px",
      }}
    >
      <Typography marginBottom="24px" fontSize="20px" fontWeight="500">
        Filters
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Distributor Field with Autocomplete */}
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel
                id="demo-multiple-checkbox-label"
                sx={{ color: "black", fontSize: "12px" }}
                shrink
              >
                Distributors
              </InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                displayEmpty
                multiple
                value={formik.values.distributor}
                onChange={(event) => {
                  const {
                    target: { value },
                  } = event;
                  formik.setFieldValue(
                    "distributor",
                    typeof value === "string" ? value.split(",") : value
                  );
                }}
                input={<OutlinedInput label="Distributors" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span style={{ color: "#aaa" }}>-Select</span>;
                  }
                  return selected.join(", ");
                }}
              >
                {distributorNames?.map((distributor: string) => (
                  <MenuItem key={distributor} value={distributor}>
                    <Checkbox
                      sx={{ color: "black" }}
                      checked={formik.values.distributor.includes(distributor)}
                    />
                    <ListItemText primary={distributor} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Rating Field */}
          <Grid item xs={12} sm={6}>
            {/* <TextField
              select
              fullWidth
              id="rating"
              name="rating"
              label="Rating"
              size="small"
              sx={{ "& .MuiInputLabel-root": {  color: "#666666", fontSize:"12px", } }}
              InputLabelProps={{ shrink: true }}
              value={formik.values.rating}
              onChange={formik.handleChange}
              error={formik.touched.rating && Boolean(formik.errors.rating)}
              helperText={formik.touched.rating && formik.errors.rating}
            >
               <MenuItem value="1" defaultChecked disabled sx={{ color: '#aaa' }}>
                Select
              </MenuItem>
              {Object.keys(ratingMapper).map((key) => (
                <MenuItem sx={{ height: "28px" }} key={key} value={key}>
                  {ratingMapper[key]}
                </MenuItem>
              ))}
            </TextField> */}

            <FormControl size="small" fullWidth>
              <InputLabel
                id="demo-multiple-checkbox-label"
                sx={{ color: "black", fontSize: "12px" }}
                shrink
              >
                Ratings
              </InputLabel>
              <Select
                labelId="rating-label"
                id="demo-multiple-checkbox"
                displayEmpty
                label="Ratings" // <-- this is important
                name="rating"
                value={formik.values.rating}
                onChange={formik.handleChange}
                error={formik.touched.rating && Boolean(formik.errors.rating)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#aaa" }}>-Select</span>;
                  }
                  return ratingMapper[selected] || selected;
                }}
              >
                <MenuItem disabled value="">
                  <span style={{ color: "#aaa" }}>Select</span>
                </MenuItem>
                {Object.keys(ratingMapper).map((key) => (
                  <MenuItem key={key} value={key}>
                    {ratingMapper[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Start DateTime Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Start from"
              type="datetime-local"
             value={
    formik.values.startTime === null || formik.values.startTime === undefined
      ? dayjs().startOf('day').format("YYYY-MM-DDTHH:mm") // Default to today
      : typeof formik.values.startTime === "string"
      ? dayjs(formik.values.startTime).format("YYYY-MM-DDTHH:mm")
      : formik.values.startTime?.$gte
      ? dayjs(formik.values.startTime.$gte).format("YYYY-MM-DDTHH:mm")
      : dayjs().startOf('day').format("YYYY-MM-DDTHH:mm") // Fallback to today
  }
   onChange={(e) =>
            handleChange(e, "startTime", (val) => {
              const isoString = val ? new Date(val).toISOString() : null;
              formik.setFieldValue("startTime", isoString);
            })
          }
  InputLabelProps={{ shrink: true }}
  sx={{
     "& .MuiInputLabel-root": { color: "black", fontSize: "12px" },
  }}
  fullWidth
  size="small"
  error={formik.touched.startTime && Boolean(formik.errors.startTime)}
  helperText={formik.touched.startTime && formik.errors.startTime}
/>
          </Grid>

          {/* End DateTime Field */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="End At"
              type="datetime-local"
              value={
                formik.values.endTime
                  ? dayjs(formik.values.endTime).format("YYYY-MM-DDTHH:mm")
                  : ""
              }
              onChange={(e) =>
            handleChange(e, "endTime", (val) => {
              const isoString = val ? new Date(val).toISOString() : null;
              formik.setFieldValue("endTime", isoString);
            })
          }
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputLabel-root": { color: "black", fontSize: "12px" },
              }}
              fullWidth
              size="small"
              error={formik.touched.endTime && Boolean(formik.errors.endTime)}
              helperText={formik.touched.endTime && formik.errors.endTime}
            />
          </Grid>

          {/* Event Type Field */}
          <Grid item xs={12} sm={6}>
            {/* <TextField
              select
              fullWidth
              placeholder="-Select"
              id="event_type"
              name="event_type"
              label="Event Type"
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                "& .MuiInputLabel-root": { color: "#666666", fontSize: "12px" },
              }}
              value={formik.values.event_type}
              onChange={formik.handleChange}
              error={
                formik.touched.event_type && Boolean(formik.errors.event_type)
              }
              helperText={formik.touched.event_type && formik.errors.event_type}
            >
              {Object.keys(eventTypeMapper).map((key) => (
                <MenuItem sx={{ height: "28px" }} key={key} value={key}>
                  {eventTypeMapper[key]}
                </MenuItem>
              ))}
            </TextField> */}
            <FormControl size="small" fullWidth>
              <InputLabel
                id="event-type-label"
                shrink
                sx={{ color: "black", fontSize: "12px" }}
              >
                Event Type
              </InputLabel>
              <Select
                labelId="event-type-label"
                id="event_type"
                displayEmpty
                name="event_type"
                value={formik.values.event_type}
                onChange={formik.handleChange}
                input={<OutlinedInput label="Event Type" />}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#aaa" }}>- Select</span>;
                  }
                  return eventTypeMapper[selected];
                }}
              >
                {Object.keys(eventTypeMapper).map((key) => (
                  <MenuItem sx={{ height: "28px" }} key={key} value={key}>
                    {eventTypeMapper[key]}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.event_type && formik.errors.event_type && (
                <FormHelperText error>
                  {formik.errors.event_type}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Broadcast Type Field */}
          <Grid item xs={12} sm={6}>
            {/* <TextField
            
              select
              fullWidth
              id="broadcast_type"
              name="broadcast_type"
              label="Broadcast Type"
              placeholder="Select"
              size="small"
              sx={{
                "& .MuiInputLabel-root": { color: "#666666", fontSize: "12px" },
              }}
              InputLabelProps={{ shrink: true }}
              value={formik.values.broadcast_type}
              onChange={formik.handleChange}
              error={
                formik.touched.broadcast_type &&
                Boolean(formik.errors.broadcast_type)
              }
              helperText={
                formik.touched.broadcast_type && formik.errors.broadcast_type
              }
            >
              {Object.keys(broadcastTypes).map((key) => (
                <MenuItem sx={{ height: "28px" }} key={key} value={key}>
                  {broadcastTypes[key]}
                </MenuItem>
              ))}
            </TextField> */}
            <FormControl size="small" fullWidth>
              <InputLabel
                id="broadcast_type"
                shrink
                sx={{ color: "black", fontSize: "12px" }}
              >
                Broadcast Type
              </InputLabel>
              <Select
                labelId="broadcast_type"
                id="broadcast_type"
                name="broadcast_type"
                displayEmpty
                value={formik.values.broadcast_type}
                onChange={formik.handleChange}
                input={<OutlinedInput label="Broadcast Type" />}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#aaa" }}>- Select</span>;
                  }
                  return broadcastTypes[selected];
                }}
              >
                {Object.keys(broadcastTypes).map((key) => (
                  <MenuItem sx={{ height: "28px" }} key={key} value={key}>
                    {broadcastTypes[key]}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.broadcast_type &&
                formik.errors.broadcast_type && (
                  <FormHelperText error>
                    {formik.errors.broadcast_type}
                  </FormHelperText>
                )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              sx={{ mb: 2 }}
              error={
                formik.touched.broadcast_regions &&
                Boolean(formik.errors.broadcast_regions)
              }
            >
              <InputLabel
                sx={{ color: "black", fontSize: "12px" }}
                id="broadcast-region-label"
                shrink
              >
                Broadcast Region*
              </InputLabel>
              <Select
                labelId="broadcast-region-label"
                label="Broadcast Region*"
                name="broadcast_regions"
                displayEmpty
                size="small"
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#666666",
                    fontSize: "12px",
                  },
                }}
                multiple
                value={formik.values.broadcast_regions || []}
                onChange={formik.handleChange}
                renderValue={(selected) =>
                  selected && selected.length > 0 ? (
                    `${selected.length} selected`
                  ) : (
                    <span style={{ color: "#aaa" }}>Select</span>
                  )
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 260,
                      overflowY: "auto",
                    },
                  },
                }}
              >
                {regions?.map((region: Region) => (
                  <MenuItem
                    sx={{ maxHeight: "25px" }}
                    key={region.id}
                    value={region.id}
                  >
                    {/* <Checkbox checked={formik.values.broadcast_regions?.includes(region?.id)} /> */}
                    <ListItemText primary={region.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Buttons: Apply & Reset */}
        <Box display="flex" gap="8px" justifyContent="flex-end" mt="16px">
         <Button
  onClick={() =>
    formik.resetForm({
      values: {
        ...formik.initialValues,
        startTime:"", 
        endTime: "", 
      },
    })
  }
  background="white"
  color="black"
>
            Reset
          </Button>
          <Button type="submit" color="white">
            Apply Filters
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default FilterPage;
